/**
 * Utility functions for the Results Page
 *
 * This module contains helper functions for price filtering and demographic facet management.
 */

import { AGE_GROUPS_BY_NAVBAR_CATEGORY, GENDER_BY_NAVBAR_CATEGORY } from "@/lib/shop-agent-client";
import { clientGeneratedFacet, copyFacet } from "@/lib/facet-provenance";

// Type for facets in search params
// Note: This matches the API type from openapi/shop_agent/client/types.gen.ts
// where values is a required field. `source`/`stage` are the facet's
// provenance (search-unification M5.2b): who set it, so relaxation never drops
// a filter the shopper chose. Copies must keep them — see lib/facet-provenance.
export type Facet = {
  name: string;
  values: string[];
  source?: "caller" | "generated" | "reconciled" | null;
  stage?: string | null;
};

export type PriceFilterParams = {
  price_min?: number | null;
  price_max?: number | null;
};

/** Strip the attribute handle prefix from a facet value if present.
 *  The backend uses double-underscore separators (e.g., "knit_style__cable_knit")
 *  and recommendations may contain either full handles or clean values.
 *  Only strips the double-underscore prefix — single underscores are part of
 *  the value itself (e.g., "fit_and_flare" should NOT become "and_flare"). */
export function trimAttributePrefix(handle: string, value: string): string {
  const prefix = handle + "__";
  return value.startsWith(prefix) ? value.substring(prefix.length) : value;
}

export function formatFilterLabel(handle: string, value: string): string {
  const cleanValue = trimAttributePrefix(handle, value);
  return String(cleanValue)
    .replace(/_/g, " ")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function formatFacetDisplayName(name: string): string {
  return name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Returns whether a facet should render as an attribute suggestion pill.
 * Excludes system facets and brand/category facets that are surfaced separately.
 */
export function shouldRenderFacetAsAttributePill(name: string): boolean {
  return !(
    name.startsWith("category_path.depth_") ||
    name === "gender" ||
    name === "age_groups" ||
    name === "brand_name" ||
    name === "is_activewear"
  );
}

/**
 * Helper function to check if a price_min value is valid.
 * Validates that the value is a number greater than 0 (not null, undefined, 0, or negative).
 * price_min: 0 is semantically meaningless, so we require > 0.
 * Follows the existing codebase convention where priceRange[0] > 0 is required.
 *
 * @param value - The price_min value to validate
 * @returns True if value is a number > 0, false otherwise
 */
export function isValidPriceMin(value: number | null | undefined): value is number {
  return typeof value === "number" && value > 0;
}

/**
 * Helper function to check if a price_max value is valid.
 * Validates that the value is a number >= 0 (not null or undefined, but allows 0 for free items).
 * price_max: 0 is semantically meaningful (filters for free items), so we allow >= 0.
 *
 * @param value - The price_max value to validate
 * @returns True if value is a number >= 0, false otherwise
 */
export function isValidPriceMax(value: number | null | undefined): value is number {
  return typeof value === "number" && value >= 0;
}

/**
 * Normalizes price bounds ensuring min <= max and validating values.
 * If min > max, swaps them. Invalidates values that don't pass validation after swap.
 *
 * @param priceMin - The minimum price value
 * @param priceMax - The maximum price value
 * @returns Object with normalized min and max values (undefined if invalid)
 */
export function normalizePriceBounds(
  priceMin?: number | null,
  priceMax?: number | null
): { min?: number; max?: number } {
  let minValue = isValidPriceMin(priceMin) ? priceMin : undefined;
  let maxValue = isValidPriceMax(priceMax) ? priceMax : undefined;

  if (typeof minValue === "number" && typeof maxValue === "number" && minValue > maxValue) {
    [minValue, maxValue] = [maxValue, minValue];

    if (!isValidPriceMin(minValue)) {
      minValue = undefined;
    }

    if (!isValidPriceMax(maxValue)) {
      maxValue = undefined;
    }
  }

  return { min: minValue, max: maxValue };
}

/**
 * Applies price filters to a params object.
 * Normalizes price bounds and adds/removes price_min and price_max fields.
 *
 * @param params - The params object to modify
 * @param priceMin - The minimum price value
 * @param priceMax - The maximum price value
 * @returns New params object with price filters applied
 */
export function applyPriceFilters<T extends object>(
  params: T,
  priceMin?: number | null,
  priceMax?: number | null
): T & PriceFilterParams {
  const next: T & PriceFilterParams = { ...params };
  const { min: normalizedMin, max: normalizedMax } = normalizePriceBounds(priceMin, priceMax);

  if (isValidPriceMin(normalizedMin ?? null)) {
    next.price_min = normalizedMin;
  } else {
    delete next.price_min;
  }

  if (isValidPriceMax(normalizedMax ?? null)) {
    next.price_max = normalizedMax;
  } else {
    delete next.price_max;
  }

  return next;
}

/**
 * Adds demographic facets (gender and age_groups) to the facets array if not already present.
 * Infers values from current search params or navbar category.
 *
 * @param facets - The original facets array
 * @param currentSearchFacets - Current search params facets to check for existing values
 * @param sessionStorageKey - Key to read navbar category from sessionStorage
 * @returns New facets array with demographic facets added if applicable
 */
export function addDemographicFacetsIfMissing(
  facets: Facet[],
  currentSearchFacets: Facet[] | undefined,
  sessionStorageKey: string
): Facet[] {
  const result = [...facets];
  let hasGenderFacet = result.some((f) => f.name === "gender");
  let hasAgeGroupsFacet = result.some((f) => f.name === "age_groups");

  // Try to get values from current search params first
  const existingGenderFacet = (currentSearchFacets || []).find((f) => f.name === "gender");
  const existingAgeGroupsFacet = (currentSearchFacets || []).find((f) => f.name === "age_groups");

  // Add gender from existing search params
  if (!hasGenderFacet && existingGenderFacet?.values?.[0]) {
    result.push({ ...existingGenderFacet, values: [existingGenderFacet.values[0]] });
    hasGenderFacet = true;
  }

  // Add age_groups from existing search params
  if (!hasAgeGroupsFacet && existingAgeGroupsFacet?.values?.length) {
    result.push(copyFacet(existingAgeGroupsFacet));
    hasAgeGroupsFacet = true;
  }

  // If we still don't have these facets, infer from navbar category
  const navbarCategory = sessionStorage.getItem(sessionStorageKey);
  if (navbarCategory) {
    // Gender mapping (uses shared constant from shop-agent-client)
    if (!hasGenderFacet) {
      const inferredGender = GENDER_BY_NAVBAR_CATEGORY[navbarCategory];
      if (inferredGender) {
        // Inferred from a navbar click that sticks in sessionStorage for the
        // whole thread — a client inference, not a filter the shopper set on
        // this search, so it stays droppable.
        result.push(clientGeneratedFacet("gender", [inferredGender]));
      }
    }

    // Age groups mapping (uses shared constant from shop-agent-client)
    if (!hasAgeGroupsFacet) {
      const inferredAgeGroups = AGE_GROUPS_BY_NAVBAR_CATEGORY[navbarCategory];
      if (inferredAgeGroups) {
        result.push(clientGeneratedFacet("age_groups", inferredAgeGroups));
      }
    }
  }

  return result;
}
